import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { useToast } from '../../hooks/use-toast';
import { Badge } from '../../components/ui/badge';
import {
  User, Building2, Mail, Phone, Calendar, CheckCircle, XCircle,
  Clock, CreditCard, TrendingUp, FileText, MapPin, Edit
} from 'lucide-react';
import { Separator } from '../../components/ui/separator';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter
} from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';

const CustomerDetail = () => {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [limitsDialogOpen, setLimitsDialogOpen] = useState(false);
  const [overdraftDialogOpen, setOverdraftDialogOpen] = useState(false);
  const [limitsReason, setLimitsReason] = useState('');
  const [limitsForm, setLimitsForm] = useState({
    pix_daily_limit: 5000,
    pix_tx_limit: 1000,
    boleto_daily_limit: 10000,
  });
  const [overdraftForm, setOverdraftForm] = useState({
    limit_total: 1000,
    interest_rate_monthly: 0.0499,
  });
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    fetchCustomerDetail();
  }, [id]);

  const fetchCustomerDetail = async () => {
    try {
      const response = await api.get(`/admin/customers/${id}/detail`);
      setData(response.data);
      const currentLimits = response.data?.operational_limits;
      const currentOverdraft = response.data?.overdraft_limit;

      setLimitsForm({
        pix_daily_limit: currentLimits?.pix_daily_limit ?? 5000,
        pix_tx_limit: currentLimits?.pix_tx_limit ?? 1000,
        boleto_daily_limit: currentLimits?.boleto_daily_limit ?? 10000,
      });

      setOverdraftForm({
        limit_total: currentOverdraft?.limit_total ?? 1000,
        interest_rate_monthly: currentOverdraft?.interest_rate_monthly ?? 0.0499,
      });
    } catch (error) {
      console.error('Error:', error);
      toast({
        variant: 'destructive',
        title: 'Erro',
        description: 'Erro ao carregar dados do cliente',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveOperationalLimits = async () => {
    if (!data?.account) return;

    if (!limitsReason.trim()) {
      toast({
        variant: 'destructive',
        title: 'Erro',
        description: 'Informe o motivo da alteração',
      });
      return;
    }

    try {
      if (data.operational_limits?.id) {
        await api.put(`/admin/limits/${data.operational_limits.id}`, {
          ...limitsForm,
          reason: limitsReason,
        });
      } else {
        await api.post('/admin/limits', {
          account_id: data.account.id,
          ...limitsForm,
        });
      }

      toast({
        title: 'Limites atualizados!',
        description: 'Limites operacionais atualizados com sucesso.',
      });
      setLimitsDialogOpen(false);
      setLimitsReason('');
      fetchCustomerDetail();
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Erro',
        description: error.response?.data?.detail || 'Erro ao atualizar limites',
      });
    }
  };

  const handleSaveOverdraftLimit = async () => {
    if (!data?.account) return;

    if (!limitsReason.trim()) {
      toast({
        variant: 'destructive',
        title: 'Erro',
        description: 'Informe o motivo da alteração',
      });
      return;
    }

    try {
      await api.post(`/admin/overdraft/${data.account.id}/set-limit`, overdraftForm);
      toast({
        title: 'Cheque especial atualizado!',
        description: 'Limite de cheque especial atualizado com sucesso.',
      });
      setOverdraftDialogOpen(false);
      setLimitsReason('');
      fetchCustomerDetail();
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Erro',
        description: error.response?.data?.detail || 'Erro ao atualizar cheque especial',
      });
    }
  };

  const handleApproveKYC = async () => {
    if (!data?.kyc) {
      toast({
        variant: 'destructive',
        title: 'Erro',
        description: 'KYC não encontrado',
      });
      return;
    }

    try {
      await api.post(`/admin/kyc/${data.kyc.id}/approve`);
      toast({
        title: 'KYC Aprovado!',
        description: 'Cliente ativado e conta criada.',
      });
      fetchCustomerDetail();
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Erro',
        description: error.response?.data?.detail || 'Erro ao aprovar KYC',
      });
    }
  };

  const handleRejectKYC = async () => {
    if (!rejectReason.trim()) {
      toast({
        variant: 'destructive',
        title: 'Erro',
        description: 'Informe o motivo da rejeição',
      });
      return;
    }

    try {
      await api.post(`/admin/kyc/${data.kyc.id}/reject`, {
        reason: rejectReason
      });
      toast({
        title: 'KYC Rejeitado',
        description: 'Cliente foi notificado.',
      });
      setRejectDialogOpen(false);
      setRejectReason('');
      fetchCustomerDetail();
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Erro',
        description: error.response?.data?.detail || 'Erro ao rejeitar KYC',
      });
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const getStatusBadge = (status) => {
    const config = {
      ACTIVE: { variant: 'default', label: 'Ativo' },
      PENDING: { variant: 'secondary', label: 'Pendente' },
      BLOCKED: { variant: 'destructive', label: 'Bloqueado' },
      APPROVED: { variant: 'default', label: 'Aprovado' },
      REJECTED: { variant: 'destructive', label: 'Rejeitado' },
      SUBMITTED: { variant: 'secondary', label: 'Em Análise' },
      NOT_STARTED: { variant: 'outline', label: 'Não Iniciado' },
    };

    const { variant, label } = config[status] || { variant: 'outline', label: status };
    return <Badge variant={variant}>{label}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="container mx-auto p-8">
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">Cliente não encontrado</p>
            <Button onClick={() => navigate('/admin/customers')} className="mt-4">
              Voltar
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { customer, account, balance, kyc, address, recent_transactions, operational_limits, overdraft_limit } = data;

  return (
    <div className="space-y-6" data-testid="customer-detail-page">
      <div className="flex items-center justify-between">
        <div>
          <Button variant="ghost" onClick={() => navigate('/admin/customers')} className="mb-2">
            ← Voltar
          </Button>
          <h1 className="text-4xl font-bold tracking-tight">{customer.name}</h1>
          <p className="text-muted-foreground mt-1">Detalhes do cliente</p>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          {account && (
            <>
              <Button
                variant="outline"
                className="rounded-full"
                onClick={() => setLimitsDialogOpen(true)}
              >
                <Edit className="mr-2 h-4 w-4" />
                Gerenciar limites
              </Button>
              <Button
                variant="outline"
                className="rounded-full"
                onClick={() => navigate('/admin/loans')}
              >
                <TrendingUp className="mr-2 h-4 w-4" />
                Empréstimos
              </Button>
            </>
          )}
          {customer.type === 'PF' ? (
            <User className="h-6 w-6 text-muted-foreground" />
          ) : (
            <Building2 className="h-6 w-6 text-muted-foreground" />
          )}
        </div>
      </div>

      {/* Customer Info */}
      <Card className="border-border/50">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Informações Básicas</CardTitle>
            <div className="flex gap-2">
              {getStatusBadge(customer.status)}
              <Badge variant="outline">{customer.type}</Badge>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="flex items-start gap-3">
              <FileText className="h-5 w-5 text-muted-foreground mt-0.5" />
              <div>
                <p className="text-sm text-muted-foreground">Documento</p>
                <p className="font-mono font-medium">{customer.document}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Mail className="h-5 w-5 text-muted-foreground mt-0.5" />
              <div>
                <p className="text-sm text-muted-foreground">E-mail</p>
                <p className="font-medium">{customer.email}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Phone className="h-5 w-5 text-muted-foreground mt-0.5" />
              <div>
                <p className="text-sm text-muted-foreground">Telefone</p>
                <p className="font-medium">{customer.phone}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Calendar className="h-5 w-5 text-muted-foreground mt-0.5" />
              <div>
                <p className="text-sm text-muted-foreground">Criado em</p>
                <p className="font-medium">{formatDate(customer.created_at)}</p>
              </div>
            </div>
          </div>

          {address && (
            <>
              <Separator className="my-4" />
              <div className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div>
                  <p className="text-sm text-muted-foreground">Endereço</p>
                  <p className="font-medium">
                    {address.street}, {address.number} {address.complement && `- ${address.complement}`}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {address.neighborhood}, {address.city} - {address.state}, {address.zip_code}
                  </p>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* KYC Status */}
      <Card className="border-border/50">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Status do KYC</CardTitle>
            {kyc ? getStatusBadge(kyc.status) : <Badge variant="outline">Não iniciado</Badge>}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {!kyc ? (
            <div className="py-8 text-center text-muted-foreground">
              <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>O cliente ainda não iniciou o processo de KYC</p>
              <p className="text-sm">Os documentos serão exibidos aqui após o envio</p>
            </div>
          ) : (
            <>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Submetido em</p>
                  <p className="font-medium">{formatDate(kyc.submitted_at)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Revisado em</p>
                  <p className="font-medium">{formatDate(kyc.reviewed_at)}</p>
                </div>
              </div>

              {/* KYC Documents */}
              {data?.kyc_documents && data.kyc_documents.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-medium">Documentos Enviados ({data.kyc_documents.length})</p>
                  <div className="grid md:grid-cols-2 gap-2">
                    {data.kyc_documents.map((doc, idx) => (
                      <div key={idx} className="flex items-center gap-2 p-3 rounded-lg bg-muted/50 border border-border/50">
                        <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                        <span className="text-sm flex-1 truncate">{doc.doc_type.replace(/_/g, ' ')}</span>
                        <a 
                          href={doc.signed_url || doc.file_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-xs text-primary hover:underline font-medium"
                        >
                          Abrir
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {kyc.rejection_reason && (
                <div className="rounded-lg bg-destructive/5 border border-destructive/20 p-4">
                  <p className="text-sm font-medium text-destructive mb-1">Motivo da rejeição:</p>
                  <p className="text-sm">{kyc.rejection_reason}</p>
                </div>
              )}

              {kyc.status === 'SUBMITTED' && (
                <div className="flex gap-2">
                  <Button
                    onClick={handleApproveKYC}
                    className="rounded-full"
                    data-testid="approve-kyc-button"
                  >
                    <CheckCircle className="mr-2 h-4 w-4" />
                    Aprovar KYC
                  </Button>
                  <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
                    <DialogTrigger asChild>
                      <Button variant="destructive" className="rounded-full">
                        <XCircle className="mr-2 h-4 w-4" />
                        Rejeitar KYC
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Rejeitar KYC</DialogTitle>
                        <DialogDescription>
                          Informe o motivo da rejeição. O cliente poderá corrigir e reenviar.
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-2">
                        <Label htmlFor="reason">Motivo</Label>
                        <Textarea
                          id="reason"
                          placeholder="Ex: Documento ilegível, data de nascimento não compatível..."
                          value={rejectReason}
                          onChange={(e) => setRejectReason(e.target.value)}
                          rows={4}
                        />
                      </div>
                      <DialogFooter>
                        <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>
                          Cancelar
                        </Button>
                        <Button variant="destructive" onClick={handleRejectKYC}>
                          Confirmar Rejeição
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>
              )}

              {kyc.status === 'APPROVED' && (
                <div className="rounded-lg bg-green-500/10 border border-green-500/20 p-4">
                  <p className="text-sm font-medium text-green-600">✓ KYC aprovado</p>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Account & Balance */}
      {account && (
        <Card className="border-border/50">
          <CardHeader>
            <CardTitle>Conta Digital</CardTitle>
            <CardDescription>Dados operacionais da conta de pagamento</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-5 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Agencia</p>
                <p className="font-mono font-medium">{account.branch_number || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Conta</p>
                <p className="font-mono font-medium">
                  {account.account_number ? `${account.account_number}-${account.account_digit || '0'}` : '-'}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Tipo</p>
                <p className="font-medium">{account.account_label || 'Conta Digital'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Classificação</p>
                <Badge variant="outline">{customer.type}</Badge>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Status</p>
                {getStatusBadge(account.status)}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {account && (
        <div className="grid md:grid-cols-3 gap-6">
          <Card className="border-border/50">
            <CardHeader>
              <CardTitle className="text-sm font-medium text-muted-foreground">Saldo Disponível</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold font-mono">{formatCurrency(balance?.available)}</p>
            </CardContent>
          </Card>
          <Card className="border-border/50">
            <CardHeader>
              <CardTitle className="text-sm font-medium text-muted-foreground">Bloqueios</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold font-mono">{formatCurrency(balance?.holds)}</p>
            </CardContent>
          </Card>
          <Card className="border-border/50">
            <CardHeader>
              <CardTitle className="text-sm font-medium text-muted-foreground">Saldo Total</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold font-mono">{formatCurrency(balance?.total)}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Limits */}
      {account && (
        <Card className="border-border/50">
          <CardHeader>
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div>
                <CardTitle>Gestão de Limites</CardTitle>
                <CardDescription>Edite limites operacionais e cheque especial deste cliente</CardDescription>
              </div>
              <div className="flex flex-wrap gap-2">
                <Dialog open={limitsDialogOpen} onOpenChange={setLimitsDialogOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" size="sm" className="rounded-full">
                      <Edit className="mr-2 h-4 w-4" />
                      Limites operacionais
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>{operational_limits ? 'Editar limites operacionais' : 'Criar limites operacionais'}</DialogTitle>
                      <DialogDescription>Defina os limites de transação para esta conta.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label>Pix Diário</Label>
                        <Input
                          type="number"
                          value={limitsForm.pix_daily_limit}
                          onChange={(e) => setLimitsForm({ ...limitsForm, pix_daily_limit: parseFloat(e.target.value) || 0 })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Pix por Transação</Label>
                        <Input
                          type="number"
                          value={limitsForm.pix_tx_limit}
                          onChange={(e) => setLimitsForm({ ...limitsForm, pix_tx_limit: parseFloat(e.target.value) || 0 })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Boleto Diário</Label>
                        <Input
                          type="number"
                          value={limitsForm.boleto_daily_limit}
                          onChange={(e) => setLimitsForm({ ...limitsForm, boleto_daily_limit: parseFloat(e.target.value) || 0 })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Motivo da alteração</Label>
                        <Textarea
                          placeholder="Descreva o motivo da alteração..."
                          value={limitsReason}
                          onChange={(e) => setLimitsReason(e.target.value)}
                          rows={3}
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setLimitsDialogOpen(false)}>
                        Cancelar
                      </Button>
                      <Button onClick={handleSaveOperationalLimits}>
                        Salvar
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>

                <Dialog open={overdraftDialogOpen} onOpenChange={setOverdraftDialogOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" size="sm" className="rounded-full">
                      <CreditCard className="mr-2 h-4 w-4" />
                      Cheque especial
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>{overdraft_limit ? 'Editar cheque especial' : 'Criar cheque especial'}</DialogTitle>
                      <DialogDescription>Defina o limite emergencial e a taxa mensal.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label>Limite Total</Label>
                        <Input
                          type="number"
                          value={overdraftForm.limit_total}
                          onChange={(e) => setOverdraftForm({ ...overdraftForm, limit_total: parseFloat(e.target.value) || 0 })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Taxa de Juros Mensal (%)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={(overdraftForm.interest_rate_monthly * 100).toFixed(2)}
                          onChange={(e) => setOverdraftForm({ ...overdraftForm, interest_rate_monthly: (parseFloat(e.target.value) || 0) / 100 })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Motivo da alteração</Label>
                        <Textarea
                          placeholder="Descreva o motivo da alteração..."
                          value={limitsReason}
                          onChange={(e) => setLimitsReason(e.target.value)}
                          rows={3}
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setOverdraftDialogOpen(false)}>
                        Cancelar
                      </Button>
                      <Button onClick={handleSaveOverdraftLimit}>
                        Salvar
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="rounded-lg border border-border/50 p-4">
                <p className="text-sm text-muted-foreground mb-2">Cheque Especial</p>
                <p className="text-xl font-bold font-mono">{formatCurrency(overdraft_limit?.limit_total)}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Taxa: {overdraft_limit ? (overdraft_limit.interest_rate_monthly * 100).toFixed(2) : '0.00'}% a.m.
                </p>
              </div>
              <div className="rounded-lg border border-border/50 p-4">
                <p className="text-sm text-muted-foreground mb-2">Pix Diário</p>
                <p className="text-xl font-bold font-mono">{formatCurrency(operational_limits?.pix_daily_limit)}</p>
              </div>
              <div className="rounded-lg border border-border/50 p-4">
                <p className="text-sm text-muted-foreground mb-2">Pix por Transação</p>
                <p className="text-xl font-bold font-mono">{formatCurrency(operational_limits?.pix_tx_limit)}</p>
              </div>
              <div className="rounded-lg border border-border/50 p-4">
                <p className="text-sm text-muted-foreground mb-2">Boleto Diário</p>
                <p className="text-xl font-bold font-mono">{formatCurrency(operational_limits?.boleto_daily_limit)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Transactions */}
      {recent_transactions && recent_transactions.length > 0 && (
        <Card className="border-border/50">
          <CardHeader>
            <CardTitle>Últimas Transações</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {recent_transactions.map((tx) => (
                <div key={tx.id} className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
                  <div>
                    <p className="font-medium text-sm">{tx.description}</p>
                    <p className="text-xs text-muted-foreground">{formatDate(tx.created_at)}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-mono font-bold">{formatCurrency(tx.amount)}</p>
                    <Badge variant="outline" className="text-xs">{tx.type}</Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CustomerDetail;
